import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {Observable} from 'rxjs';
import {ErrorModel} from '../../app.model';
import {
  ApiId,
  DistributionAccount,
  DistributionAccountModel, TargetSystemId
} from '../../distribution-account/distribution-account.model';
import {AccountConfigurationWrapper, isMultiShareConfigSupported, ShareAccount} from '../../segment-multi-share/domain';
import {SegmentShareService} from '../../segment-multi-share/segment-share.service';
import {TargetAccount} from '../../target/target.model';
import {TargetService, TtdCategory, TtdPartner} from '../../target/target.service';
import {Segment} from '../segment.model';
import {SubSink} from 'subsink';
import {ActionsSubject, Store} from '@ngrx/store';
import {selectSegmentDetailsWithTargets} from '../segments.state';
import {ACTION_TYPES} from '../../app.types';
import {ofType} from '@ngrx/effects';

@Component({
  selector: 'app-segments-targets',
  styleUrls: ['share.component.scss'],
  template: `
    <form (submit)="shareSegment()">
      <button mat-icon-button
              id="button-dialog-close"
              class="mat-icon-button-small"
              data-cy="button-dialog-close"
              [mat-dialog-close]="true">
        <mat-icon>clear</mat-icon>
      </button>
      <h5 mat-dialog-title>Share Segment</h5>
      <div mat-dialog-content class="mat-body-1">
        Select a target you want to share this segment to.
        <mat-form-field id="segment-account-filter"
                        class="example-full-width">
          <input #quicksearch
                 matInput
                 [autofocus]="true"
                 placeholder="Filter targets"
                 value="">
        </mat-form-field>
        <div id="accounts-to-share-list">
          <ng-container
            *ngFor="let target of nonSelectedTargets | shareSegmentListFilter: quicksearch.value; let j = index;">
            {{target.targetName}}
            <mat-list>
              <mat-list-item
                *ngFor="let account of target.accounts | shareSegmentListFilter: quicksearch.value; let i = index;"
                data-cy="element-target-list-item">

                <ng-container [ngSwitch]="account.apiId">

                  <app-segment-share-item-ttd *ngSwitchCase="apiId.TTD"
                                              [accountValue]="account"
                                              [categories]="ttdCategories | async"></app-segment-share-item-ttd>

                  <app-segment-share-item *ngSwitchCase="apiId.ACTIVE_AGENT"
                                          [accountValue]="account"
                                          [system]="target"></app-segment-share-item>

                  <app-segment-share-item *ngSwitchDefault
                                          data-cy="segment-share-item"
                                          [accountValue]="account"
                                          [system]="target"></app-segment-share-item>

                </ng-container>
              </mat-list-item>
            </mat-list>
          </ng-container>
        </div>
      </div>
      <div mat-dialog-actions>
        <p *ngIf="errorMessage"
           class="error"
           id="error-feedback"
           data-cy="element-error-message">{{errorMessage}}</p>
        <button mat-raised-button type="reset">Reset</button>
        <button mat-raised-button
                type="submit"
                color="primary"
                id="button-segment-share"
                data-cy="button-segment-share"
                *ngIf="!hasErrors"
                [disabled]="buttonDisabled">
          <span *ngIf="!buttonDisabled">Share</span>
          <mat-icon *ngIf="buttonDisabled" id="load-icon">autorenew</mat-icon>
        </button>
        <button mat-raised-button
                type="button"
                color="primary"
                *ngIf="hasErrors"
                [mat-dialog-close]="true">Close window
        </button>
      </div>
    </form>

  `
})
export class SegmentShareComponent implements OnInit, OnDestroy {
  @ViewChild('quicksearch') quicksearchInput!: ElementRef<HTMLInputElement>;
  buttonDisabled = false;
  errorMessage!: string;
  hasErrors = false;
  selectedSegment!: Segment;
  allTargets: TargetAccount[] = [];
  nonSelectedTargets: TargetAccount[] = [];
  targetsForSegment: TargetAccount[] = [];
  subsink = new SubSink();
  apiId = ApiId;
  ttdCategories!: Observable<TtdCategory[]>;
  ttdPartners!: Observable<TtdPartner[]>;

  constructor(
    private store: Store,
    private actions: ActionsSubject,
    private targetActions: TargetService,
    private router: Router,
    private segmentShareService: SegmentShareService,
    private dialogRef: MatDialogRef<SegmentShareComponent>) {
  }

  ngOnInit() {
    this.subsink.sink = this.store.select(selectSegmentDetailsWithTargets)
      .subscribe(state => {
        if (!state.details?.segment) {
          // ignore, we're closing the tab
        } else {
          this.selectedSegment = state.details.segment;
          this.targetsForSegment = state.details.accounts;
          this.allTargets = state.allTargets;
          this.nonSelectedTargets = this.filterSharedTargets(this.allTargets, this.targetsForSegment);
        }
      });

    this.subsink.sink = this.actions.pipe(ofType(ACTION_TYPES.TARGETS_SHARED_FOR_SEGMENT)).subscribe(action => {
      const targetsErrors = (action as unknown as { errors: ErrorModel[] }).errors;
      if (targetsErrors.length > 0) {
        this.hasErrors = true;
        let errorResponse;
        try {
          errorResponse = JSON.parse(targetsErrors[0].message)?.message;
        } catch (err) {
          errorResponse = targetsErrors[0].message;
        }
        this.errorMessage = errorResponse;
      } else {
        this.hasErrors = false;
        this.dialogRef.close();
      }
    });

    this.ttdCategories = this.targetActions.getTtdCategories();
    this.ttdPartners = this.targetActions.getTtdPartners();
  }

  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  shareSegment() {
    this.buttonDisabled = true;

    const {unsupported, supported} = this.nonSelectedTargets
      .reduce<{ unsupported: DistributionAccount[], supported: DistributionAccount[] }>((agg, system) => {
        if (system.targetId && isMultiShareConfigSupported(system.targetId)) {
          agg.supported = agg.supported.concat(system.accounts);
        } else {
          agg.unsupported = agg.unsupported.concat(system.accounts);
        }
        return agg;
      }, {unsupported: [], supported: []});

    const allSelectedAccountsSupportMultishare = !unsupported.some(account => account.selected)
      && supported.some(account => account.selected);

    if (allSelectedAccountsSupportMultishare) {
      this.segmentShareService.reset();
      this.segmentShareService.addSegmentToSelection(this.selectedSegment.id);
      this.nonSelectedTargets.forEach(system => {
        if (system.targetId && isMultiShareConfigSupported(system.targetId)) {
          system.accounts.forEach(account => {
            if (account.selected) {
              this.segmentShareService.addAccountToSelection(account.id);
            }
          });
        }
      });

      this.router.navigate(['segmentsmultishare']).then(() => {
      });
      this.dialogRef.close();
      return;
    } else {
      this.targetActions.shareSegmentWithTargets(this.nonSelectedTargets, this.selectedSegment);
    }
  }

  private filterSharedTargets(allTargets: TargetAccount[], targetsForSegment: TargetAccount[]): TargetAccount[] {
    if (typeof targetsForSegment !== 'undefined' && targetsForSegment !== null && targetsForSegment.length > 0) {
      return allTargets.map((target: TargetAccount) => {
        return {
          ...target,
          accounts: target.accounts
            .filter((account: DistributionAccount) => !this.isAccountInTargets(account, targetsForSegment))
            .map(old => ({...old}))
        };
      }).filter((targetModel: TargetAccount) => {
        return targetModel.accounts.length > 0;
      });
    }
    // make a copy of array 'cause it's mutated elsewhere...
    return [
      ...allTargets
        .map(old => ({
          ...old,
          accounts: old.accounts.map(oldAcc => ({...oldAcc}))
        }))];
  }

  private isAccountInTargets(account: DistributionAccount, targets: TargetAccount[]): boolean {
    let isInSelectedTargets = false;
    targets.forEach((target) => {
      target.accounts.forEach((targetAccount) => {

        if (targetAccount.id === account.id) {
          isInSelectedTargets = true;
          return;
        }
      });
    });
    return isInSelectedTargets;
  }

}
